import type { ReactNode } from 'react';
import { LabelWithValue } from '@/components/common/LabelWithValue';
import { formatDisplayDate } from '@/utils/date';
import { formatTaka } from '@/utils/number';
import {
  PCMP_ACCOUNT_KIND_BY_PAYMENT_TYPE,
  PCMP_PARTIALLY_RETURNED_LABEL,
  PCMP_PAYMENT_TYPE_LABELS,
} from '../../constants/pcmpProposal.constants';
import type { PcmpProposalPayment } from '../../types/pcmpProposal.types';
import { PcmpProposalSection } from './PcmpProposalSection';
import { PcmpStatusPill } from './PcmpStatusPill';

interface PcmpPaymentCardProps {
  payment: PcmpProposalPayment;
  actions?: ReactNode;
}

/** The bank or wallet account on file; nothing for cash and gift. */
export function PcmpPaymentAccountDetails({
  payment: { paymentType, account },
}: {
  payment: Pick<PcmpProposalPayment, 'paymentType' | 'account'>;
}) {
  const kind = account?.kind ?? PCMP_ACCOUNT_KIND_BY_PAYMENT_TYPE[paymentType];
  if (kind === null) return null;
  if (!account) {
    return <p className="text-sm text-muted-foreground">No account on file.</p>;
  }
  if (kind === 'BANK') {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <LabelWithValue label="Account holder">{account.holderName}</LabelWithValue>
        <LabelWithValue label="Bank">{account.bankName}</LabelWithValue>
        <LabelWithValue label="Branch">{account.branchName}</LabelWithValue>
        <LabelWithValue label="Account no">
          {account.accountNo ? <span className="font-mono">{account.accountNo}</span> : null}
        </LabelWithValue>
        <LabelWithValue label="Routing no">
          {account.routingNo ? <span className="font-mono">{account.routingNo}</span> : null}
        </LabelWithValue>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <LabelWithValue label="Account holder">{account.holderName}</LabelWithValue>
      <LabelWithValue label="Wallet number">
        {account.mobileNo ? <span className="font-mono">{account.mobileNo}</span> : null}
      </LabelWithValue>
    </div>
  );
}

export function PcmpPaymentCard({ payment, actions }: PcmpPaymentCardProps) {
  // Part of the payment came back and the rest can still follow (LDS-765).
  const isPartlyReturned = payment.returnedAmount > 0 && payment.returnableAmount > 0;
  return (
    <PcmpProposalSection
      title="Payment"
      actions={
        <>
          {isPartlyReturned ? (
            <PcmpStatusPill tone="warning">{PCMP_PARTIALLY_RETURNED_LABEL}</PcmpStatusPill>
          ) : null}
          <PcmpStatusPill tone="info">
            {PCMP_PAYMENT_TYPE_LABELS[payment.paymentType]}
          </PcmpStatusPill>
          {actions}
        </>
      }
    >
      {payment.isOnBehalf ? (
        <p className="rounded-lg bg-warning px-4 py-2 text-sm text-warning-foreground">
          Paid on behalf of the teacher
          {payment.onBehalfName ? `, to ${payment.onBehalfName}` : ''}.
        </p>
      ) : null}
      <PcmpPaymentAccountDetails payment={payment} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <LabelWithValue label="Proposed">{formatTaka(payment.proposedAmount)}</LabelWithValue>
        <LabelWithValue label={payment.returnedAmount > 0 ? 'Paid (net of returns)' : 'Paid'}>
          {formatTaka(payment.paidAmount)}
        </LabelWithValue>
        {payment.returnedAmount > 0 ? (
          <>
            <LabelWithValue label="Returned">{formatTaka(payment.returnedAmount)}</LabelWithValue>
            <LabelWithValue label="Left to return">
              {formatTaka(payment.returnableAmount)}
            </LabelWithValue>
          </>
        ) : null}
        <LabelWithValue label="Payment date">
          {payment.paymentDate ? formatDisplayDate(payment.paymentDate) : null}
        </LabelWithValue>
        <LabelWithValue label="Reference no">{payment.referenceNo}</LabelWithValue>
      </div>
      {payment.comments ? (
        <LabelWithValue label="Payment Note">
          <span className="whitespace-pre-line font-normal">{payment.comments}</span>
        </LabelWithValue>
      ) : null}
    </PcmpProposalSection>
  );
}
